import { FC, useEffect } from 'react';
import {
  Field,
  Icon,
  Avatar,
  Select,
  SocialMediaIcon,
  SocialMediaIconProps,
  Alert,
} from '@kontentino/ui';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { useQuery } from 'react-query';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import { useTranslation } from 'react-i18next';
import { useToast } from 'app/hooks/useToast';
import { queryKey } from 'constants/queryKey';
import config from 'config';
import { WebCrawlResultSchema } from 'app/modules/brandHub/types';
import WebCrawlLinkField from 'app/modules/brandHub/components/WebCrawlLinkField';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faChartLine } from '@fortawesome/pro-regular-svg-icons/faChartLine';
import PageTypeUtils from 'app/utils/pageType';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import Logger from 'utils/logger';

const MAX_ANALYTICS_PROFILES = 5;

export const analyticsProfileSchema = z.object({
  profileId: z.string(),
  pageType: z.number(),
});

export const brandProfileFormSchema = z
  .object({
    webCrawl: WebCrawlResultSchema.optional().nullable(),
    analyticsProfiles: z.array(analyticsProfileSchema).optional().default([]),
  })
  .refine(
    (data) => {
      // While analytics is hidden, only require webCrawl. Once analytics is
      // wired up again the original OR check applies.
      if (!config.BRAND_HUB_ANALYTICS_ENABLED) return Boolean(data.webCrawl);

      return data.webCrawl || data?.analyticsProfiles.length > 0;
    },
    () => ({
      message: config.BRAND_HUB_ANALYTICS_ENABLED
        ? 'Either analytics profile(s) or website URL must be provided'
        : 'Website URL is required',
      path: ['_errors'],
    }),
  );

export type BrandProfileFormValues = z.infer<typeof brandProfileFormSchema>;

export type FormErrorType = {
  _errors: {
    message: string;
    type: string;
  };
};

type Props = {
  onSubmit: (data: BrandProfileFormValues) => void;
  isLoading?: boolean;
  formRef?: (form: UseFormReturn<BrandProfileFormValues>) => void;
};

const BrandHubProfileForm: FC<Props> = ({ onSubmit, isLoading, formRef }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const analyticsProfiles = useQuery(queryKey.brandHubAnalyticsProfiles(), () =>
    BrandHubApi.getAnalyticsProfiles(),
  );

  const form = useForm<BrandProfileFormValues>({
    resolver: zodResolver(brandProfileFormSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      webCrawl: undefined,
      analyticsProfiles: [],
    },
  });

  // Expose form to parent component using useEffect to ensure it's always up-to-date
  useEffect(() => {
    if (formRef) {
      formRef(form);
    }
  }, [form, formRef]);

  function handleSubmit(data: BrandProfileFormValues) {
    onSubmit(data);
  }

  function handleInvalidSubmit(errors: unknown) {
    Logger.warn('[BrandHubProfileForm] validation failed', errors);
  }

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
      id="brand-hub-profile-form"
    >
      <section className="tw-space-y-4 tw-pb-4">
        {(form.formState.errors as FormErrorType)?._errors?.message && (
          <Alert variant="warning" data-cy="brand-hub_suggest-profile_warning">
            {(form.formState.errors as FormErrorType)?._errors.message}
          </Alert>
        )}
        <Field.Group>
          <Field.Label required>
            <Icon icon={faGlobe} className="tw-mr-1" />
            {t('webLink')}
          </Field.Label>
          <Controller
            control={form.control}
            name="webCrawl"
            render={({ field }) => (
              <WebCrawlLinkField
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  form.trigger();
                }}
                placeholder={t('brandProfileWebsiteLinkPlaceholder')}
                dataNamePrefix="brand-hub_suggest-profile_web-crawl-link"
                autoFetch
              />
            )}
          />
          {!!form.formState.errors.webCrawl?.message && (
            <Field.Error>
              <>{form.formState.errors.webCrawl?.message}</>
            </Field.Error>
          )}
        </Field.Group>
        {config.BRAND_HUB_ANALYTICS_ENABLED && (
          <Field.Group>
            <Field.Label required>
              <Icon icon={faChartLine} className="tw-mr-1" />
              {t('analyticsProfile')}
            </Field.Label>

            <Controller
              control={form.control}
              name="analyticsProfiles"
              render={({ field }) => {
                const profileOptions =
                  analyticsProfiles.data?.edges.map(({ node }) => ({
                    value: node.id,
                    label: `${node.name} ${
                      node.fans ? `(${node.fans} ${t('fans')})` : ''
                    }`,
                    type: node._type,
                    picture: node.picture,
                  })) || [];

                return (
                  <div
                    data-name="brand-hub_suggest-profile_analytics-profile_select-wrapper"
                    data-cy="brand-hub_suggest-profile_analytics-profile_select-wrapper"
                  >
                    <Select
                      menuPortalTarget={document.body}
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      styles={{
                        menuPortal: (styles) => ({
                          ...styles,
                          zIndex: 1010,
                        }),
                      }}
                      value={form.watch('analyticsProfiles')?.map((profile) => {
                        const profileOption = profileOptions.find(
                          (opt) => opt.value === profile.profileId,
                        );

                        if (!profileOption) {
                          return {
                            value: profile.profileId,
                            label: 'Unknown profile',
                            type: profile.pageType,
                            picture: undefined,
                          };
                        }

                        return {
                          value: profile.profileId,
                          label: profileOption.label,
                          type: profile.pageType,
                          picture: profileOption.picture,
                        };
                      })}
                      isClearable
                      placeholder={t('selectAnalyticsProfile')}
                      formatOptionLabel={(option) => (
                        <div className="tw-flex tw-items-center">
                          <div className="tw-relative">
                            <Avatar
                              size={26}
                              name={option.label}
                              src={option.picture || undefined}
                              className="tw-rounded-md"
                            />
                            <SocialMediaIcon
                              type={
                                PageTypeUtils.getName(
                                  option.type,
                                ) as SocialMediaIconProps['type']
                              }
                              size={getYtLogoGuidelineSize(12, option.type)}
                              className="tw-absolute tw-bottom-0 tw-right-0"
                            />
                          </div>
                          <span className="tw-ml-2">{option.label}</span>
                        </div>
                      )}
                      onChange={(selectedOptions) => {
                        if (selectedOptions.length > MAX_ANALYTICS_PROFILES) {
                          toast(
                            t('maxAnalyticsProfilesForBrandProfileReached'),
                            'warning',
                          );
                          const limitedOptions = selectedOptions.slice(
                            0,
                            MAX_ANALYTICS_PROFILES,
                          );
                          const analyticsProfiles = limitedOptions.map(
                            (option) => ({
                              profileId: option.value,
                              pageType: option.type,
                            }),
                          );
                          field.onChange(analyticsProfiles);
                          form.trigger();
                          return;
                        }

                        const analyticsProfiles = selectedOptions.map(
                          (option) => ({
                            profileId: option.value,
                            pageType: option.type,
                          }),
                        );
                        field.onChange(analyticsProfiles);
                        form.trigger();
                      }}
                      options={profileOptions}
                      isLoading={analyticsProfiles.isLoading}
                      isOptionDisabled={(option) => {
                        const selectedProfiles =
                          form.watch('analyticsProfiles') || [];
                        const isAlreadySelected = selectedProfiles.some(
                          (profile) => profile.profileId === option.value,
                        );
                        return (
                          !isAlreadySelected &&
                          selectedProfiles.length >= MAX_ANALYTICS_PROFILES
                        );
                      }}
                    />
                  </div>
                );
              }}
            />
            <Field.Caption>
              {t('analyticsProfilePostsWillBeUsedToGenerateBrandProfile')}
            </Field.Caption>
          </Field.Group>
        )}
      </section>
    </form>
  );
};

export default BrandHubProfileForm;
